// src/components/tuition/ClassroomManagementStudio.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  FaArrowLeft,
  FaCheck,
  FaClock,
  FaComments,
  FaCopy,
  FaExternalLinkAlt,
  FaPaperPlane,
  FaShieldAlt,
  FaSignOutAlt,
  FaTimes,
  FaTimesCircle,
  FaUserClock,
  FaUsers,
  FaVideo,
} from "react-icons/fa";
import { toast } from "react-toastify";
import LiveKitClassroom from "../classroom/LiveKitClassroom";
import HostWaitingRoomControls from "./HostWaitingRoomControls";
import api from "../../api/httpClient";

export default function ClassroomManagementStudio({
  roomCode,
  scheduledClass,
  isHost = true,
  facultyName = "",
  onClose,
}) {
  const [activeTab, setActiveTab] = useState("waiting-room"); // "waiting-room" | "attendees" | "chat" | "info"
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingKnocksCount, setPendingKnocksCount] = useState(0);
  const [liveParticipants, setLiveParticipants] = useState([]);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    {
      sender: "System",
      text: "ClassNX Management Studio initialized. Waiting room and live controls are ready.",
      time: "Just now",
    },
  ]);
  const [newChatText, setNewChatText] = useState("");
  const classroomDataSenderRef = useRef(null);

  const hostDisplayName =
    facultyName ||
    localStorage.getItem("currentUserName") ||
    localStorage.getItem("userName") ||
    "Faculty Host";

  // Lecture Duration Stopwatch
  useEffect(() => {
    const timer = setInterval(() => {
      setDurationSeconds((sec) => sec + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Format Duration seconds into HH:MM:SS or MM:SS
  const formatTimer = (totalSec) => {
    const hrs = Math.floor(totalSec / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    const secs = totalSec % 60;
    const pad = (n) => String(n).padStart(2, "0");
    if (hrs > 0) return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
    return `${pad(mins)}:${pad(secs)}`;
  };

  const studentDirectJoinUrl = `${window.location.origin}/classroom/join/${roomCode}`;

  const handleCopyDirectLink = () => {
    navigator.clipboard?.writeText(studentDirectJoinUrl);
    setCopiedLink(true);
    toast.success("Student Direct Join link copied to clipboard!");
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const handleCopyMeetingCode = () => {
    navigator.clipboard?.writeText(roomCode);
    setCopiedCode(true);
    toast.success(`Meeting ID ${roomCode} copied!`);
    setTimeout(() => setCopiedCode(false), 2500);
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    const clean = newChatText.trim();
    if (!clean) return;
    const msg = {
      sender: hostDisplayName,
      text: clean,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };
    try {
      if (!classroomDataSenderRef.current) throw new Error("Classroom connection is not ready.");
      await classroomDataSenderRef.current({ type: "CHAT", sender: hostDisplayName, text: clean });
      setChatMessages((prev) => [...prev, msg]);
      setNewChatText("");
    } catch (error) {
      toast.error(`Message not sent: ${error?.message || "Please reconnect."}`);
    }
  };

  const handleEndClass = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to end this live class? This will close the session for all attending students."
    );
    if (!confirmed) return;

    try {
      await api.post(`/api/v2/classroom/rooms/${encodeURIComponent(roomCode)}/end`).catch(() => {});
    } catch {
      // Ignored if unsupported or already finalized
    }

    toast.info("Live class ended.");
    onClose?.();
  };

  const handleLeaveStudio = () => {
    const confirmed = window.confirm(
      "Return to the portal? The classroom session will stay active for remaining participants."
    );
    if (confirmed) {
      onClose?.();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex h-screen w-screen flex-col overflow-hidden bg-slate-950 text-white font-sans">
      {/* Top ClassNX Management Bar */}
      <header className="h-16 shrink-0 border-b border-slate-800 bg-slate-900/95 px-4 sm:px-6 flex items-center justify-between backdrop-blur-md z-30 shadow-md">
        {/* Left: Branding & Class Details */}
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={handleLeaveStudio}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-700 bg-slate-800/80 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-slate-700 hover:text-white transition shadow-sm shrink-0"
            title="Return to ClassNX Portal"
          >
            <FaArrowLeft className="text-[11px]" />
            <span className="hidden md:inline">Portal</span>
          </button>

          <div className="flex items-center gap-2 shrink-0">
            <img
              src={`${process.env.PUBLIC_URL || ""}/logo.png`}
              alt="ClassNX Logo"
              className="h-8 w-8 rounded-xl object-cover shadow-sm ring-1 ring-sky-500/30"
            />
            <span className="font-black text-sm tracking-tight hidden lg:inline">
              Class<span className="text-sky-400">NX</span> Studio
            </span>
          </div>

          <div className="h-5 w-px bg-slate-800 hidden sm:block shrink-0" />

          {/* Title & Live Status */}
          <div className="min-w-0 flex flex-col justify-center">
            <div className="flex items-center gap-2">
              <span className="flex h-2.5 w-2.5 relative shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500" />
              </span>
              <h1 className="text-xs sm:text-sm font-bold text-white truncate max-w-[220px] sm:max-w-xs md:max-w-md">
                {scheduledClass?.topic || "Live Coaching Session"}
              </h1>
              <span className="hidden sm:inline-block rounded-full bg-rose-500/20 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-rose-400 border border-rose-500/30 shrink-0">
                LIVE
              </span>
            </div>
            <p className="text-[11px] text-slate-400 truncate hidden sm:block">
              {scheduledClass?.subject || "Online Class"} &bull; Host: {hostDisplayName} &bull; Room: <strong className="text-slate-300">{roomCode}</strong>
            </p>
          </div>
        </div>

        {/* Center: Live Duration Clock */}
        <div className="hidden lg:flex items-center gap-2 rounded-full bg-slate-950/80 px-4 py-1.5 text-xs font-mono font-bold text-sky-400 border border-slate-800 shadow-inner">
          <FaClock className="text-slate-400 text-xs" />
          <span>Duration: {formatTimer(durationSeconds)}</span>
        </div>

        {/* Right: Quick Actions & Studio Controls */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {/* Quick Copy Meeting ID */}
          <button
            type="button"
            onClick={handleCopyMeetingCode}
            className="hidden sm:inline-flex items-center gap-1.5 rounded-xl border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-700 transition"
            title="Copy Meeting ID"
          >
            {copiedCode ? <FaCheck className="text-emerald-400" /> : <FaCopy />}
            <span className="font-mono">{roomCode}</span>
          </button>

          {/* Quick Copy Student Join Link */}
          <button
            type="button"
            onClick={handleCopyDirectLink}
            className="inline-flex items-center gap-1.5 rounded-xl bg-sky-600/90 hover:bg-sky-500 px-3 py-1.5 text-xs font-bold text-white transition shadow-sm"
            title="Copy Direct Student Join Link"
          >
            {copiedLink ? <FaCheck className="text-white" /> : <FaExternalLinkAlt />}
            <span className="hidden md:inline">Invite Link</span>
          </button>

          {/* Toggle Sidebar Button */}
          <button
            type="button"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`relative inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold transition border ${
              sidebarOpen
                ? "border-indigo-500/40 bg-indigo-600/30 text-indigo-300"
                : "border-slate-700 bg-slate-800 text-slate-300 hover:text-white"
            }`}
            title={sidebarOpen ? "Hide Management Sidebar" : "Open Management Sidebar"}
          >
            <FaShieldAlt />
            <span className="hidden sm:inline">Manage</span>
            {pendingKnocksCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-[9px] font-black text-slate-950 animate-pulse">
                {pendingKnocksCount}
              </span>
            )}
          </button>

          {/* End Class Button */}
          <button
            type="button"
            onClick={handleEndClass}
            className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-red-700 to-rose-700 hover:from-red-600 hover:to-rose-600 px-3.5 py-1.5 text-xs font-bold text-white shadow-md transition"
            title="End class for everyone"
          >
            <FaTimesCircle />
            <span className="hidden sm:inline">End Class</span>
          </button>
        </div>
      </header>

      {/* Main Studio Body: Video Stage + Side Management Deck */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Left: Main Stage (LiveKit Video / Audio) */}
        <div className="flex-1 flex flex-col p-2 sm:p-4 overflow-hidden min-w-0">
          <div className="flex-1 rounded-2xl sm:rounded-3xl border border-slate-800 bg-slate-900/60 overflow-hidden flex flex-col relative shadow-2xl">
            <LiveKitClassroom
              meetingCode={roomCode}
              guestToken=""
              isHost={true}
              displayName={hostDisplayName}
              initialMicEnabled={true}
              initialCamEnabled={true}
              onLeave={handleLeaveStudio}
              onToggleChat={() => {
                setSidebarOpen(true);
                setActiveTab("chat");
              }}
              onToggleParticipants={() => {
                setSidebarOpen(true);
                setActiveTab("attendees");
              }}
              showChat={sidebarOpen && activeTab === "chat"}
              showParticipants={sidebarOpen && activeTab === "attendees"}
              onParticipantsChanged={setLiveParticipants}
              onDataSenderReady={(sender) => { classroomDataSenderRef.current = sender; }}
              onChatMessage={(message) => {
                setChatMessages((previous) => [...previous, {
                  id: message.id,
                  sender: message.sender || "Participant",
                  text: message.text,
                  time: new Date(message.sentAt || Date.now()).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                }]);
                toast.info(`${message.sender || "Participant"}: ${message.text}`);
              }}
              allowStudentAudio={scheduledClass?.allowStudentAudio ?? true}
              allowStudentVideo={scheduledClass?.allowStudentVideo ?? false}
            />
          </div>
        </div>

        {/* Right: Embedded Faculty Management Console */}
          <aside className={`${sidebarOpen ? "w-80 sm:w-96 border-l" : "w-0 border-l-0 pointer-events-none"} overflow-hidden border-slate-800 bg-slate-900/95 flex flex-col shrink-0 z-20 backdrop-blur-xl transition-[width] duration-200`}>
            {/* Sidebar Navigation Tabs */}
            <div className="flex items-center border-b border-slate-800 bg-slate-950/60 p-1.5 gap-1 shrink-0">
              <button
                type="button"
                onClick={() => setActiveTab("waiting-room")}
                className={`flex-1 relative flex items-center justify-center gap-1.5 rounded-xl py-2 px-1 text-xs font-bold transition ${
                  activeTab === "waiting-room"
                    ? "bg-slate-800 text-white shadow"
                    : "text-slate-400 hover:text-slate-200 hover:bg-slate-900"
                }`}
              >
                <FaUserClock className={activeTab === "waiting-room" ? "text-sky-400" : ""} />
                <span className="truncate">Waiting</span>
                {pendingKnocksCount > 0 && (
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-[9px] font-black text-slate-950 animate-pulse">
                    {pendingKnocksCount}
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("attendees")}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2 px-1 text-xs font-bold transition ${
                  activeTab === "attendees"
                    ? "bg-slate-800 text-white shadow"
                    : "text-slate-400 hover:text-slate-200 hover:bg-slate-900"
                }`}
              >
                <FaUsers className={activeTab === "attendees" ? "text-indigo-400" : ""} />
                <span className="truncate">Roster</span>
                <span className="rounded-full bg-slate-700 px-1.5 py-0.2 text-[9px] text-slate-300">
                  {Math.max(1, liveParticipants.length)}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab("chat")}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2 px-1 text-xs font-bold transition ${
                  activeTab === "chat"
                    ? "bg-slate-800 text-white shadow"
                    : "text-slate-400 hover:text-slate-200 hover:bg-slate-900"
                }`}
              >
                <FaComments className={activeTab === "chat" ? "text-emerald-400" : ""} />
                <span className="truncate">Chat</span>
              </button>

              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="rounded-lg p-2 text-slate-400 hover:text-white hover:bg-slate-800 transition"
                title="Collapse sidebar"
              >
                <FaTimes className="text-xs" />
              </button>
            </div>

            {/* Tab 1: Waiting Room (Knock Queue) */}
              <div className={`${activeTab === "waiting-room" ? "flex" : "hidden"} min-w-80 sm:min-w-96 flex-1 flex-col p-3 overflow-hidden`}>
                <HostWaitingRoomControls
                  roomCode={roomCode}
                  isHost={true}
                  embedded={true}
                  onPendingCountChange={setPendingKnocksCount}
                />
              </div>

            {/* Tab 2: Attendees Roster */}
            {activeTab === "attendees" && (
              <div className="flex-1 flex flex-col p-3 overflow-hidden">
                <div className="flex items-center justify-between pb-2 border-b border-slate-800 mb-2">
                  <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                    Connected Attendees ({Math.max(1, liveParticipants.length)})
                  </h3>
                </div>

                <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                  {liveParticipants.length === 0 ? (
                    <div className="rounded-xl border border-slate-800 bg-slate-950/80 p-3 text-xs flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center font-bold text-white shrink-0">
                        {hostDisplayName.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-white truncate">{hostDisplayName} (You)</p>
                        <p className="text-[10px] text-indigo-400 font-semibold">Faculty / Host</p>
                      </div>
                      <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[9px] font-bold text-emerald-400 border border-emerald-500/30">
                        Active
                      </span>
                    </div>
                  ) : (
                    liveParticipants.map((p) => (
                      <div
                        key={p.identity}
                        className={`flex items-center gap-2.5 rounded-xl border p-2.5 text-xs transition ${
                          p.isLocal
                            ? "border-indigo-500/40 bg-indigo-950/30"
                            : "border-slate-800 bg-slate-950/80 hover:border-slate-700"
                        }`}
                      >
                        <div
                          className={`h-7 w-7 rounded-full text-white font-bold flex items-center justify-center text-xs shrink-0 ${
                            p.isLocal ? "bg-indigo-600" : "bg-slate-700"
                          }`}
                        >
                          {p.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-slate-200 truncate text-xs">
                            {p.name}
                            {p.isLocal ? " (You)" : ""}
                          </p>
                          <p className="text-[10px] text-slate-500">
                            {p.isLocal ? "Host" : "Joined Student"}
                          </p>
                        </div>
                        <span className="text-[10px] font-bold text-emerald-400 shrink-0">Online</span>
                      </div>
                    ))
                  )}
                </div>

                {/* Direct Share Link helper at bottom of roster */}
                <div className="pt-3 border-t border-slate-800 mt-2">
                  <button
                    type="button"
                    onClick={handleCopyDirectLink}
                    className="w-full flex items-center justify-center gap-2 rounded-xl bg-slate-800 hover:bg-slate-700 py-2 px-3 text-xs font-bold text-slate-200 transition"
                  >
                    <FaCopy /> Copy Student Invite Link
                  </button>
                </div>
              </div>
            )}

            {/* Tab 3: Lecture Chat */}
            {activeTab === "chat" && (
              <div className="flex-1 flex flex-col p-3 overflow-hidden">
                <div className="flex-1 overflow-y-auto space-y-2.5 pr-1">
                  {chatMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`rounded-2xl p-2.5 text-xs ${
                        msg.sender === hostDisplayName
                          ? "bg-indigo-600/20 border border-indigo-500/30 ml-4 text-right"
                          : msg.sender === "System"
                          ? "bg-slate-950/80 border border-slate-800 text-slate-400 text-center text-[10px]"
                          : "bg-slate-950/90 border border-slate-800 mr-4 text-left"
                      }`}
                    >
                      {msg.sender !== "System" && (
                        <div className="flex items-center justify-between text-[10px] font-bold text-slate-400 mb-1">
                          <span>{msg.sender}</span>
                          <span>{msg.time}</span>
                        </div>
                      )}
                      <p className="text-slate-200 break-words">{msg.text}</p>
                    </div>
                  ))}
                </div>

                <form onSubmit={handleSendMessage} className="pt-3 border-t border-slate-800 flex gap-2">
                  <input
                    type="text"
                    value={newChatText}
                    onChange={(e) => setNewChatText(e.target.value)}
                    placeholder="Broadcast message to class..."
                    className="flex-1 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-slate-100 focus:outline-none focus:border-indigo-500"
                  />
                  <button
                    type="submit"
                    className="rounded-xl bg-indigo-600 px-3.5 py-2 text-xs font-bold text-white hover:bg-indigo-500 transition shadow"
                    title="Send message"
                  >
                    <FaPaperPlane />
                  </button>
                </form>
              </div>
            )}
          </aside>
      </div>
    </div>
  );
}
